<template>
  <div class="z-index-10-fg single-lead-popup">
    <div class="popup-content">
      <div class="empty-spacer"></div>
      <div v-if="loadingLead.value || !lead.LEADID" class="loading">
        <div class="close-lead-icon">
          <img @click="showSingleLead = null" src="../assets/close.svg" />
        </div>

        <img v-if="loadingLead.value" src="../assets/spinner.svg" />
        <p v-else>There was an error loading this lead.</p>
      </div>
      <div v-else class="lead-container">
        <div class="leads-table">
          <LeadHeader
            :lead="lead"
            :isOpen="true"
            @toggleLead="showSingleLead = null"
            @showAssignSalesperson="(leadId) => (showAssignSalesperson = leadId)"
            @showAddInteraction="(leadId) => (showAddInteraction = leadId)"
            @showAddCompletion="(leadId) => (showAddCompletion = leadId)"
            @showDeleteConfirmation="(leadId) => (showDeleteConfirmation = leadId)"
            @showUncompleteConfirmation="(leadId) => (showUncompleteConfirmation = leadId)"
            @showDelayAlert="(leadId) => (showDelayAlert = leadId)"
            @showUnlinkConfirmation="(leadId) => (showUnlinkConfirmation = leadId)"
            @showSendEmail="(templateId) => (showSendEmail = { lead: lead, templateId: templateId })"
            @showSendContractorList="(lead) => (showSendContractorList = lead)"
            @showLinkContact="(leadId) => (showLinkContact = leadId)"
            @showMarkAsDead="(leadId) => (showMarkAsDeadConfirmation = leadId)" />
        </div>
        <AssignSalesperson v-if="showAssignSalesperson" :leadid="showAssignSalesperson" @closePopup="showAssignSalesperson = null" />
        <AddInteraction v-if="showAddInteraction" :leadid="showAddInteraction" @closePopup="showAddInteraction = null" />
        <AddCompletion v-if="showAddCompletion" :leadid="showAddCompletion" @closePopup="showAddCompletion = null" />
        <LinkContact v-if="showLinkContact" @closePopup="showLinkContact = false" :leadid="showLinkContact" />
        <DelayAlert v-if="showDelayAlert" :leadid="showDelayAlert" @closePopup="showDelayAlert = null" />
        <Confirmation
          v-if="showUnlinkConfirmation"
          @confirm="unlinkContact(showUnlinkConfirmation)"
          @cancel="showUnlinkConfirmation = null"
          header="Unlink Contact"
          message="Are you sure you want to unlink the contact from this lead?"
          confirmText="Unlink"
          cancelText="Cancel"
          :serious="true" />
        <Confirmation
          v-if="showDeleteConfirmation"
          @confirm="
            deleteLead(showDeleteConfirmation);
            showSingleLead = null;
          "
          @cancel="showDeleteConfirmation = null"
          header="Delete Lead"
          message="Are you sure you want to delete this lead? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          :serious="true" />
        <Confirmation
          v-if="showUncompleteConfirmation"
          @confirm="uncompleteLead(showUncompleteConfirmation)"
          @cancel="showUncompleteConfirmation = null"
          header="Uncomplete Lead"
          message="Are you sure you want to uncomplete this lead?"
          confirmText="Uncomplete"
          cancelText="Cancel"
          :serious="true" />
        <Confirmation
          v-if="showMarkAsDeadConfirmation"
          @confirm="markLeadAsDead(showMarkAsDeadConfirmation)"
          @cancel="showMarkAsDeadConfirmation = null"
          header="Mark Lead as Dead"
          message="Are you sure you want to mark this lead as dead? This will change the score to dead and create a 'Marked as Dead' interaction."
          confirmText="Mark as Dead"
          cancelText="Cancel"
          :serious="true" />
        <SendEmail v-if="showSendEmail" :lead="showSendEmail.lead" :templateId="showSendEmail.templateId" @close="showSendEmail = null" />
        <SendContractorList v-if="showSendContractorList" :lead="showSendContractorList" @close="showSendContractorList = null" />
      </div>
    </div>
  </div>
  <div class="screen-overlay-gray z-index-10-bg"></div>
</template>

<script>
import LeadHeader from './LeadHeader.vue';
import useLeadsQuery from '@/composables/useLeadsQuery';
import useSingleLeadQuery from '@/composables/useSingleLeadQuery';
import useUsersQuery from '@/composables/useUsersQuery';
import AssignSalesperson from '../components/AssignSalesperson.vue';
import AddInteraction from '../components/AddInteraction.vue';
import AddCompletion from '../components/AddCompletion.vue';
import LinkContact from '../components/LinkContact.vue';
import Confirmation from '../components/Confirmation.vue';
import DelayAlert from '../components/DelayAlert.vue';
import useLeadMutate from '@/composables/useLeadMutate';
import useInteractionMutate from '@/composables/useInteractionMutate';
import useAlerts from '@/composables/useAlerts';
import useCurrentUser from '@/composables/useCurrentUser';
import SendEmail from '../components/SendEmail.vue';
import SendContractorList from '../components/SendContractorList.vue';

export default {
  name: 'SingleLead',
  components: {
    LeadHeader,
    AssignSalesperson,
    AddInteraction,
    AddCompletion,
    LinkContact,
    Confirmation,
    DelayAlert,
    SendEmail,
    SendContractorList,
  },
  setup() {
    const { deleteLead, updateLead } = useLeadMutate();
    const { addInteraction } = useInteractionMutate();
    const { addAlert } = useAlerts();
    const { user } = useCurrentUser();
    const { loadingLead, showSingleLead } = useSingleLeadQuery;
    const { allUsers } = useUsersQuery();

    return {
      leadsIncludingSingleLead: useLeadsQuery.leadsIncludingSingleLead,
      allUsers,
      loadingLead,
      deleteLead,
      updateLead,
      addInteraction,
      addAlert,
      user,
      showSingleLead,
    };
  },
  data() {
    return {
      showDelayAlert: null,
      showAssignSalesperson: null,
      showAddInteraction: null,
      showAddCompletion: null,
      showLinkContact: null,
      showUnlinkConfirmation: null,
      showDeleteConfirmation: null,
      showUncompleteConfirmation: null,
      showMarkAsDeadConfirmation: null,
      showSendEmail: null,
      showSendContractorList: null,
    };
  },
  computed: {
    lead() {
      return this.leadsIncludingSingleLead.find((l) => l.LEADID == this.showSingleLead) || {};
    },
  },
  methods: {
    navigateToContact(lead) {
      if (lead.CONTACTID) {
        this.$router.push({ name: 'contacts', params: { contactid: lead.CONTACTID } });
      } else if (lead.COMPANYID) {
        this.$router.push({ name: 'companies', params: { companyid: lead.COMPANYID } });
      }
      this.showSingleLead = null;
    },
    contactLinkAction(lead) {
      if (!lead.company && !lead.contact) {
        this.showLinkContact = lead.LEADID;
      } else if (!this.shouldHideUnlinkContact(lead)) {
        this.showUnlinkConfirmation = lead.LEADID;
      }
    },
    shouldHideUnlinkContact(lead) {
      if (!lead.company && !lead.contact) return true;
      if (!lead.NAME) return true;
      if (this.user.userid == lead.ASSIGNEDID) return false;
      if (this.user.leadsAdmin) return false;
      return true;
    },
    leadCompletionType(lead) {
      if (!lead.COMPLETE) return 'In Progress';
      if (lead.interactions.some((interaction) => interaction.TYPE == 'Confirmed Order')) return 'Confirmed Order';
      return 'Completed';
    },
    async unlinkContact(leadid) {
      const lead = this.leadsIncludingSingleLead.find((lead) => lead.LEADID === leadid);
      lead.company = null;
      lead.contact = null;
      lead.LINKMETHOD = null;
      this.showUnlinkConfirmation = null;

      const leadToUpdate = {
        COMPANYID: null,
        CONTACTID: null,
        LINKMETHOD: null,
      };

      await this.updateLead(leadid, leadToUpdate);
    },
    async uncompleteLead(leadid) {
      const lead = this.leadsIncludingSingleLead.find((lead) => lead.LEADID === leadid);
      lead.COMPLETE = false;
      lead.COMPLETEDATE = null;
      this.showUncompleteConfirmation = null;

      const leadToUpdate = {
        COMPLETE: false,
        COMPLETEDATE: null,
      };

      await this.updateLead(leadid, leadToUpdate);
    },
    async markLeadAsDead(leadid) {
      this.showMarkAsDeadConfirmation = null;

      try {
        // Find the lead to update
        const lead = this.leadsIncludingSingleLead.find((lead) => lead.LEADID === leadid);
        if (!lead) {
          this.addAlert('Error finding lead', 'high', 4);
          return;
        }

        // Update the lead's score to 'dead'
        const leadToUpdate = {
          SCORE: 'dead',
        };

        // Create a new interaction
        const newInteraction = {
          LEADID: leadid,
          NOTE: 'Marked as dead.',
          TYPE: 'Marked as Dead',
          DATE: Date.now().toString(),
          SALESPERSONID: this.user.userid,
        };

        const newInteractionResponse = await this.addInteraction(newInteraction);

        // Update the local lead object
        lead.SCORE = 'dead';

        // Add the interaction to the lead's interactions array
        if (newInteractionResponse && newInteractionResponse.addInteraction) {
          newInteraction.INTERACTIONID = newInteractionResponse.addInteraction;
          lead.interactions.push(newInteraction);
        }

        await this.updateLead(leadid, leadToUpdate);

        this.addAlert('Lead marked as dead', 'low', 3);
      } catch (error) {
        console.error('Error marking lead as dead:', error);
        this.addAlert('Error marking lead as dead', 'high', 4);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.single-lead-popup {
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  padding: 0;
  overflow: hidden;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: transparent;

  .popup-content {
    overflow-y: auto;
    &::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
    padding: 0;
    height: 100%;
    position: relative;

    .empty-spacer {
      height: 5rem;
    }

    .loading {
      width: calc(100% - 10rem);
      height: calc(100% - 10rem);
      display: flex;
      justify-content: center;

      align-items: center;
      background-color: #fff;
      margin: 0rem 5rem;
      box-shadow: $shadow1;
      border-radius: $borderRadius10;
      position: relative;

      .close-lead-icon {
        position: absolute;
        top: 1rem;
        right: 1rem;

        img {
          width: 1.6rem;
          height: 1.6rem;
          cursor: pointer;
        }
      }

      img {
        width: 4rem;
      }
    }
  }
}

.leads-table .table-row {
  padding-top: 0.2rem;
  border-bottom: none;
}
</style>
